'use client'
import Image from 'next/image'
import { useEffect, useRef, useState } from 'react'
import HubSpotForm from '../common/HubSpotForm'

import { cn } from '@/utils/twMerge'
import { debounce } from '@/utils/lodashReplacements'
import { PrismicRichText } from '@prismicio/react'
import { PrismicNextImage } from '@prismicio/next'

type ResourcesAsideProps = {
  typesData: string[]
  topicsData: string[]
  portalId: string
  formId: string
  complianceTypesData: any
  complianceTopicsData: any
  newsLetterOptInFormTitle: any
  setTypeData: (value: string) => void
  setTopicData: (value: string) => void
  setSearchText: (value: string) => void
}

const ResourcesAside = ({
  typesData,
  topicsData,
  portalId,
  formId,
  setTypeData,
  setTopicData,
  setSearchText,
  complianceTypesData,
  complianceTopicsData,
  newsLetterOptInFormTitle,
}: ResourcesAsideProps) => {
  const [localSearchText, setLocalSearchText] = useState('')
  const debouncedSetSearchTextRef = useRef(debounce((value: string) => setSearchText(value), 300))

  useEffect(() => {
    debouncedSetSearchTextRef.current(localSearchText)
  }, [localSearchText])

  return (
    <aside className="h-min lg:sticky lg:top-[100px] lg:col-span-2">
      <div className="relative [&>img]:absolute [&>img]:left-5 [&>img]:top-[14px]">
        <input
          type="text"
          placeholder="Search"
          aria-label="search"
          value={localSearchText}
          onChange={(e) => setLocalSearchText(e.target.value)}
          className="h-11 w-full appearance-none rounded-md border border-primary-ink-6 pl-10 pr-2 text-primary-ink-100 placeholder-primary-ink-30 focus:border-primary-ink-6 focus:outline-none focus:ring-0"
        />
        <Image src="/images/search.svg" width={14} height={14} alt="" aria-hidden="true" />
      </div>

      <div className="flex flex-col gap-6 py-8">
        {complianceTypesData.map((item: any, index: number) => {
          const isActive = typesData.includes(item.type)
          return (
            <button
              key={index}
              type="button"
              aria-pressed={isActive}
              className={cn(
                'resources-aside-compliance-post-type-item flex w-full cursor-pointer items-center gap-2 border-0 bg-transparent pl-3 text-left',
                isActive && 'resources-aside-compliance-post-type-item-active',
              )}
              onClick={() => setTypeData(item.type)}>
              <PrismicNextImage field={item.icon} className="shrink-0" fallbackAlt="" />
              <span className="text-base leading-none text-primary-ink-60">{item.type}</span>
            </button>
          )
        })}
      </div>

      <div>
        <p className="mb-4 font-halyardDisplay text-sm">Filter by Tag</p>
        <div className="flex flex-wrap gap-4">
          {complianceTopicsData.map((item: any, index: number) => {
            const isActive = topicsData.includes(item.type)
            return (
              <button
                type="button"
                aria-pressed={isActive}
                className={cn(
                  'cursor-pointer rounded-md border-0 bg-primary-ink-6 px-3 py-1 font-halyardText text-[15px] transition-colors duration-200 hover:bg-primary-green-20',
                  isActive && 'bg-primary-green-20',
                )}
                key={index}
                onClick={() => setTopicData(item.type)}>
                <span className="relative top-0.5 block">{item.type}</span>
              </button>
            )
          })}
        </div>
      </div>

      <div className="mt-11 rounded-md border border-primary-ink-6 bg-white/30 px-4 py-6 shadow-light backdrop-blur-25">
        <div className="mb-4 font-halyardDisplay text-lg">
          <PrismicRichText field={newsLetterOptInFormTitle} />
        </div>
        <HubSpotForm portalId={portalId} formId={formId} type={`opt-in-form`} />
      </div>
    </aside>
  )
}

export default ResourcesAside
